/**
 * Dashboard — "Mes candidatures" full-page view.
 *
 * 3 tabs:
 *  - Offres   : list of saved job offers with best score + application status
 *  - CVs      : list of CV versions with associated offers + best scores
 *  - Tableau  : combined Offre × CV × Score × Statut table
 *
 * Phase 5.4: Application tracking per offer (statuses persisted in localStorage)
 * Phase 5.5: Per-CV export button (sequential PDF via /api/pdf/export)
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, FileText, BarChart2, Star,
  ChevronRight, Download, Loader2, CheckCircle2, XCircle,
  Clock, Send, Trophy,
} from 'lucide-react';
import { useJobOffers } from './hooks/useJobOffers';
import type { ApplicationStatus } from './hooks/useJobOffers';
import { useI18n } from './contexts/I18nContext';
import { cn } from './lib/utils';

// Read CV versions meta from localStorage directly (same key as useCvState)
interface CvMeta { id: string; name: string; }

function readCvMeta(): CvMeta[] {
  try {
    const raw = localStorage.getItem('cv_templates_meta');
    if (!raw) return [{ id: 'default', name: 'Mon CV' }];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CvMeta[]) : [{ id: 'default', name: 'Mon CV' }];
  } catch {
    return [{ id: 'default', name: 'Mon CV' }];
  }
}

// ─── Status config ────────────────────────────────────────────────────────────

function getStatusConfig(t: (k: string) => string): Record<ApplicationStatus, { label: string; color: string; icon: React.ElementType }> {
  return {
    draft:     { label: t('dashboard.statusDraft'),     color: 'text-white/40 bg-white/5 border-white/10',              icon: FileText },
    sent:      { label: t('dashboard.statusSent'),      color: 'text-blue-300 bg-blue-500/10 border-blue-500/20',        icon: Send },
    interview: { label: t('dashboard.statusInterview'), color: 'text-violet-300 bg-violet-500/10 border-violet-500/20',  icon: Clock },
    rejected:  { label: t('dashboard.statusRejected'),  color: 'text-red-300 bg-red-500/10 border-red-500/20',           icon: XCircle },
    offer:     { label: t('dashboard.statusOffer'),     color: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20', icon: Trophy },
  };
}

const STATUS_ORDER: ApplicationStatus[] = ['draft', 'sent', 'interview', 'rejected', 'offer'];

function StatusBadge({ status, t }: { status: ApplicationStatus; t: (k: string) => string }) {
  const cfg = getStatusConfig(t)[status];
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold', cfg.color)}>
      <Icon className="size-3" />
      {cfg.label}
    </span>
  );
}

function StatusSelect({
  current, onChange, t,
}: { current: ApplicationStatus; onChange: (s: ApplicationStatus) => void; t: (k: string) => string }) {
  const statusConfig = getStatusConfig(t);
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value as ApplicationStatus)}
      className="text-xs bg-[#111] border border-white/10 rounded-lg px-2 py-1.5 text-white/70 focus:outline-none focus:border-white/30 cursor-pointer"
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s}>{statusConfig[s].label}</option>
      ))}
    </select>
  );
}

function scoreColor(score: number | null | undefined): string {
  if (score == null) return 'text-white/30';
  if (score >= 70) return 'text-emerald-400';
  if (score >= 50) return 'text-amber-400';
  return 'text-red-400';
}

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

// ─── Export helper ────────────────────────────────────────────────────────────

async function exportCvPdf(cvId: string, cvName: string): Promise<void> {
  // Read CV data from localStorage
  let cvData: unknown;
  try {
    const raw = localStorage.getItem('cv_all_templates_data');
    const all = raw ? JSON.parse(raw) : {};
    cvData = all[cvId] ?? all['default'] ?? Object.values(all)[0];
  } catch { /* ignore */ }
  if (!cvData) throw new Error('Données CV introuvables pour cet export.');

  // Read layout preference for this CV (fallback to global, then 'classique')
  const savedLayout = localStorage.getItem(`cv_layout_template_${cvId}`)
    ?? localStorage.getItem('cv_layout_template');
  const layout = (savedLayout === 'ats' || savedLayout === 'moderne' || savedLayout === 'classique' || savedLayout === 'pro')
    ? savedLayout
    : 'classique';

  const filename = `CV_${cvName.replace(/[^a-zA-Z0-9À-ÿ _-]/g, '_')}.pdf`;
  const res = await fetch('/api/pdf/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cvData, layout, options: { pageMode: true }, filename }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as { error?: string }).error ?? `Export failed: ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

type Tab = 'offers' | 'cvs' | 'table';

export function Dashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('offers');
  const [cvMeta] = useState<CvMeta[]>(() => readCvMeta());
  const [exportingCvId, setExportingCvId] = useState<string | null>(null);

  const {
    jobOffers,
    cvxjobEntries,
    applications,
    toggleFavorite,
    removeOffer,
    setApplicationStatus,
  } = useJobOffers();

  // derived
  const appMap = useMemo(() => {
    const m = new Map<string, ApplicationStatus>();
    for (const a of applications) m.set(a.jobOfferId, a.status);
    return m;
  }, [applications]);

  const getBestForJob = (jobId: string) => {
    const entries = cvxjobEntries.filter((e) => e.jobOfferId === jobId && e.atsScore !== undefined);
    if (!entries.length) return null;
    const best = entries.reduce((a, b) => ((a.atsScore ?? 0) > (b.atsScore ?? 0) ? a : b));
    return { score: best.atsScore as number, cvVersionId: best.cvVersionId };
  };

  const getBestForCv = (cvId: string) => {
    const scores = cvxjobEntries.filter((e) => e.cvVersionId === cvId && e.atsScore !== undefined).map((e) => e.atsScore as number);
    return scores.length ? Math.max(...scores) : null;
  };

  const getJobsForCv = (cvId: string) => {
    const jobIds = new Set(cvxjobEntries.filter((e) => e.cvVersionId === cvId).map((e) => e.jobOfferId));
    return jobOffers.filter((o) => jobIds.has(o.id));
  };

  // sorted offers: favorites first, then date desc
  const sortedOffers = useMemo(() => [...jobOffers].sort((a, b) => {
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
  }), [jobOffers]);

  const handleExportCv = async (cvId: string, cvName: string) => {
    setExportingCvId(cvId);
    try {
      await exportCvPdf(cvId, cvName);
    } catch {
      // silently ignore — user will notice the download didn't start
    } finally {
      setExportingCvId(null);
    }
  };

  // Batch export: all visible CVs sequentially
  const [batchExporting, setBatchExporting] = useState(false);
  const handleBatchExport = async () => {
    setBatchExporting(true);
    for (const cv of cvMeta) {
      try { await exportCvPdf(cv.id, cv.name); } catch { /* skip */ }
    }
    setBatchExporting(false);
  };

  const TABS = [
    { id: 'offers' as Tab, label: t('dashboard.tabOffers'), icon: Briefcase },
    { id: 'cvs' as Tab,    label: t('dashboard.tabCvs'),    icon: FileText },
    { id: 'table' as Tab,  label: t('dashboard.tabTable'),  icon: BarChart2 },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10 bg-[#0a0a0a]/95 sticky top-0 z-20 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate('/app')}
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="size-4" />
          {t('dashboard.back')}
        </button>
        <div className="w-px h-4 bg-white/10" />
        <h1 className="text-base font-bold">{t('dashboard.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          {jobOffers.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-white/10 text-white/50">
              {jobOffers.length} offre{jobOffers.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 py-3 border-b border-white/10">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all',
              tab === id ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/5',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full">

        {/* ── Tab: Offres ── */}
        {tab === 'offers' && (
          <div className="space-y-3">
            {sortedOffers.length === 0 ? (
              <EmptyState
                icon={Briefcase}
                title={t('dashboard.emptyOffers')}
                subtitle={t('dashboard.emptyOffersHint')}
                cta={t('dashboard.openApp')}
                onCta={() => navigate('/app')}
              />
            ) : (
              sortedOffers.map((offer) => {
                const best = getBestForJob(offer.id);
                const appStatus = appMap.get(offer.id) ?? 'draft';
                const tested = new Set(cvxjobEntries.filter((e) => e.jobOfferId === offer.id).map((e) => e.cvVersionId)).size;
                const bestCvName = best ? (cvMeta.find((c) => c.id === best.cvVersionId)?.name ?? null) : null;

                return (
                  <div key={offer.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <button
                          type="button"
                          onClick={() => toggleFavorite(offer.id)}
                          className={cn('mt-0.5 shrink-0 transition-colors', offer.isFavorite ? 'text-amber-400' : 'text-white/20 hover:text-amber-400/60')}
                        >
                          <Star className="size-4" fill={offer.isFavorite ? 'currentColor' : 'none'} />
                        </button>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{offer.title}</p>
                          <p className="text-xs text-white/50 mt-0.5">{offer.company} · {formatDate(offer.dateAdded)}</p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            {best ? (
                              <span className={cn('text-xs font-bold flex items-center gap-1', scoreColor(best.score))}>
                                <BarChart2 className="size-3" />
                                {best.score}/100 {bestCvName && <span className="text-white/40 font-normal">({bestCvName})</span>}
                              </span>
                            ) : (
                              <span className="text-xs text-white/25">{t('dashboard.notAnalyzed')}</span>
                            )}
                            {tested > 0 && (
                              <span className="text-xs text-white/30">{tested} version{tested > 1 ? 's' : ''} testée{tested > 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusSelect
                          current={appStatus}
                          onChange={(s) => setApplicationStatus(offer.id, s)}
                          t={t}
                        />
                        <button
                          type="button"
                          onClick={() => navigate('/app')}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition-colors"
                        >
                          {t('dashboard.adapt')} <ChevronRight className="size-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeOffer(offer.id)}
                          className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title={t('dashboard.delete')}
                        >
                          <XCircle className="size-3.5" />
                        </button>
                      </div>
                    </div>
                    {/* Status badge */}
                    {appStatus !== 'draft' && (
                      <div className="mt-3 pt-3 border-t border-white/[0.06]">
                        <StatusBadge status={appStatus} t={t} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── Tab: CVs ── */}
        {tab === 'cvs' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-white/40">{cvMeta.length} version{cvMeta.length > 1 ? 's' : ''} de CV</p>
              {cvMeta.length > 1 && (
                <button
                  type="button"
                  onClick={handleBatchExport}
                  disabled={batchExporting}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 disabled:opacity-50 transition-colors"
                >
                  {batchExporting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                  {t('dashboard.exportAll')}
                </button>
              )}
            </div>
            {cvMeta.map((cv) => {
              const best = getBestForCv(cv.id);
              const associatedOffers = getJobsForCv(cv.id);

              return (
                <div key={cv.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 text-white/40 shrink-0" />
                        <p className="font-semibold text-sm truncate">{cv.name}</p>
                      </div>
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        {best !== null ? (
                          <span className={cn('text-xs font-bold flex items-center gap-1', scoreColor(best))}>
                            <BarChart2 className="size-3" />
                            {t('dashboard.bestScore').replace('{score}', String(best))}
                          </span>
                        ) : (
                          <span className="text-xs text-white/25">{t('dashboard.noScore')}</span>
                        )}
                        {associatedOffers.length > 0 && (
                          <span className="text-xs text-white/30">
                            {associatedOffers.length} offre{associatedOffers.length > 1 ? 's' : ''} associée{associatedOffers.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {associatedOffers.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {associatedOffers.slice(0, 4).map((o) => {
                            const entry = cvxjobEntries.find((e) => e.cvVersionId === cv.id && e.jobOfferId === o.id);
                            return (
                              <span key={o.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">
                                {o.company}
                                {entry?.atsScore !== undefined && (
                                  <span className={cn('font-bold', scoreColor(entry.atsScore))}>{entry.atsScore}</span>
                                )}
                              </span>
                            );
                          })}
                          {associatedOffers.length > 4 && (
                            <span className="text-[10px] text-white/30 px-2 py-0.5">+{associatedOffers.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => void handleExportCv(cv.id, cv.name)}
                        disabled={exportingCvId === cv.id}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 disabled:opacity-50 transition-colors"
                      >
                        {exportingCvId === cv.id ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                        {t('dashboard.export')}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/app')}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition-colors"
                      >
                        {t('dashboard.edit')} <ChevronRight className="size-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Tab: Tableau ── */}
        {tab === 'table' && (
          <div>
            {cvxjobEntries.length === 0 ? (
              <EmptyState
                icon={BarChart2}
                title={t('dashboard.emptyTable')}
                subtitle={t('dashboard.emptyTableHint')}
                cta={t('dashboard.openApp')}
                onCta={() => navigate('/app')}
              />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/[0.02]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white/50 whitespace-nowrap">{t('dashboard.colOffer')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white/50 whitespace-nowrap">{t('dashboard.colCv')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white/50 whitespace-nowrap">{t('dashboard.colScore')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white/50 whitespace-nowrap">{t('dashboard.colAdaptedCv')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white/50 whitespace-nowrap">{t('dashboard.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.05]">
                    {cvxjobEntries.map((entry) => {
                      const offer = jobOffers.find((o) => o.id === entry.jobOfferId);
                      const cv = cvMeta.find((c) => c.id === entry.cvVersionId);
                      const adaptedCv = entry.adaptedCvId ? cvMeta.find((c) => c.id === entry.adaptedCvId) : null;
                      const appStatus = appMap.get(entry.jobOfferId) ?? 'draft';

                      return (
                        <tr key={entry.id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-sm truncate max-w-[160px]">{offer?.title ?? '—'}</div>
                            <div className="text-xs text-white/40 truncate">{offer?.company}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-white/70 truncate max-w-[120px]">{cv?.name ?? '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            {entry.atsScore !== undefined ? (
                              <span className={cn('font-bold text-sm', scoreColor(entry.atsScore))}>
                                {entry.atsScore}/100
                              </span>
                            ) : <span className="text-white/25 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {adaptedCv ? (
                              <span className="flex items-center gap-1 text-xs text-emerald-400">
                                <CheckCircle2 className="size-3" />{adaptedCv.name}
                              </span>
                            ) : <span className="text-white/25 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <StatusSelect
                              current={appStatus}
                              onChange={(s) => setApplicationStatus(entry.jobOfferId, s)}
                              t={t}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function EmptyState({
  icon: Icon, title, subtitle, cta, onCta,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  cta?: string;
  onCta?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-white/30">
      <Icon className="size-12" />
      <div className="text-center">
        <p className="text-base font-semibold text-white/50">{title}</p>
        <p className="text-sm mt-1 max-w-xs">{subtitle}</p>
      </div>
      {cta && onCta && (
        <button
          type="button"
          onClick={onCta}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/70 text-sm font-semibold transition-colors"
        >
          {cta} <ChevronRight className="size-4" />
        </button>
      )}
    </div>
  );
}
